import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import back_app from '../../lib/back_app';
import CreatorHeader from '../../components/creator/CreatorHeader';
import PlatformIcon from '../../components/PlatformIcon';

import xdate from '../../../../../util/date.js';

type Connection = {
  id: string;
  platform: string;
  platform_username: string;
  connected_at: string;
  is_active: boolean;
  metadata?: any;
};

export default function SocialConnect() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [connecting, setConnecting] = useState<string | null>(null);

  useEffect(() => {
    console.log('🔍 SocialConnect mounted, fetching connections...');
    fetchConnections();
  }, []);

  
  useEffect(() => {
    const handleFocus = () => {
      console.log('👁️ Window focused, refreshing connections...');
      fetchConnections();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const fetchConnections = async () => {
    try {
      console.log('📡 Fetching connections...');

      const result = await back_app.social_connections();

      console.log('📦 Result:', result);

      if (!result.err) {
        const allConnections = Array.isArray(result) ? result : result.connections || [];
        console.log('✅ Connections loaded:', allConnections);
        setConnections(allConnections);

        allConnections.forEach((conn: Connection) => {
          console.log(`  ${conn.platform}: ${conn.is_active ? '✅ Active' : '❌ Inactive'} - ${conn.platform_username}`);
        });
      } else {
        console.log('⚠️ API returned err');
        setConnections([]);
      }
    } catch (error) {
      console.error('❌ Error fetching connections:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (platform: 'facebook' | 'instagram') => {
    try {
      console.log(`🔗 Connecting to ${platform}...`);
      setConnecting(platform);

      const result = await back_app.social_auth_url(platform);
      console.log(`📥 ${platform} login response:`, result);

      if (!result.err && result.auth_url) {
        console.log(`✅ Redirecting to OAuth...`);
        window.location.href = result.auth_url;
      } else {
        console.error('❌ Failed to get auth URL');
        alert('Failed to initiate connection');
        setConnecting(null);
      }
    } catch (error) {
      console.error(`❌ Connection error for ${platform}:`, error);
      navigate('/login');
      alert('Failed to connect');
      setConnecting(null);
    }
  };

  const handleDisconnect = async (connectionId: string, platform: string) => {
    if (!confirm(`Disconnect ${platform}? You'll need to reconnect to publish.`)) {
      return;
    }

    try {
      console.log(`🔌 Disconnecting ${platform} (${connectionId})...`);
      
      const result = await back_app.social_disconnect({id: connectionId});

      if (result.err) {
        console.error('Disconnect error:', result.err);
        alert('Failed to disconnect');
      } else {
        console.log('Disconnected successfully');
        fetchConnections(); 
      }
    } catch (error) {
      console.error('❌ Disconnect error:', error);
      alert('Failed to disconnect');
    }
  };

  const isConnected = (platform: string): boolean => {
    const connected = connections.some(c => c.platform === platform && c.is_active);
    console.log(`🔍 isConnected(${platform}):`, connected);
    return connected;
  };

  const getConnection = (platform: string): Connection | undefined => {
    const conn = connections.find(c => c.platform === platform && c.is_active);
    console.log(`🔍 getConnection(${platform}):`, conn);
    return conn;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading connections...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CreatorHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => navigate('/creator/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <span>←</span>
            <span>Back to Dashboard</span>
          </button>

          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Social Media Connections
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Connect your social media accounts to publish videos directly from Sponly
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {}
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 p-6 sm:p-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-xl flex items-center justify-center">
                    <PlatformIcon platform="instagram" className="w-8 h-8 sm:w-10 sm:h-10"/>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white">Instagram</h3>
                    <p className="text-xs sm:text-sm text-white/80">Reels, Feed & Stories</p>
                  </div>
                </div>
                {isConnected('instagram') && (
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 sm:p-6">
              {isConnected('instagram') ? (
                <div>
                  <div className="mb-4">
                    <div className="text-xs sm:text-sm text-gray-500 mb-1">Connected as</div>
                    <div className="font-semibold text-gray-900 text-sm sm:text-base">
                      @{getConnection('instagram')?.platform_username || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Connected {xdate.fmt_lbl_short(getConnection('instagram')?.connected_at)}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <button
                      onClick={() => handleConnect('instagram')}
                      disabled={connecting === 'instagram'}
                      className="w-full px-4 py-2.5 border-2 border-pink-500 text-pink-600 rounded-lg font-semibold hover:bg-pink-50 disabled:opacity-50 text-sm sm:text-base"
                    >
                      {connecting === 'instagram' ? 'Reconnecting...' : 'Reconnect'}
                    </button>
                    <button
                      onClick={() => handleDisconnect(getConnection('instagram')!.id, 'Instagram')}
                      className="w-full px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm sm:text-base"
                    >
                      Disconnect
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Features:</h4>
                    <ul className="space-y-1.5 text-xs sm:text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <span className="text-green-600 mt-0.5">✓</span>
                        <span>Publish Reels automatically</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-600 mt-0.5">✓</span>
                        <span>Share to Feed & Stories</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-600 mt-0.5">✓</span>
                        <span>Maximum reach & engagement</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-600 mt-0.5">✓</span>
                        <span>Perfect for 9:16 videos</span>
                      </li>
                    </ul>
                  </div>

                  <button
                    onClick={() => handleConnect('instagram')}
                    disabled={connecting === 'instagram'}
                    className="w-full px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white rounded-lg font-bold hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base"
                  >
                    {connecting === 'instagram' ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Connecting...</span>
                      </>
                    ) : (
                      <>
                        <span>Connect Instagram</span>
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd"/>
                        </svg>
                      </>
                    )}
                  </button>

                  <div className="mt-3 text-xs text-gray-500 bg-blue-50 p-3 rounded-lg">
                    <strong>Note:</strong> You need an Instagram Business Account connected to a Facebook Page.
                  </div>
                </div>
              )}
            </div>
          </div>

          {}
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="bg-gradient-to-br from-blue-600 to-blue-400 p-6 sm:p-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-xl flex items-center justify-center">
                    <PlatformIcon platform="facebook" className="w-8 h-8 sm:w-10 sm:h-10"/>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white">Facebook</h3>
                    <p className="text-xs sm:text-sm text-white/80">Pages, Reels & Stories</p>
                  </div>
                </div>
                {isConnected('facebook') && (
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 sm:p-6">
              {isConnected('facebook') ? (
                <div>
                  <div className="mb-4">
                    <div className="text-xs sm:text-sm text-gray-500 mb-1">Connected as</div>
                    <div className="font-semibold text-gray-900 text-sm sm:text-base">
                      {getConnection('facebook')?.platform_username || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Connected {xdate.fmt_lbl_short(getConnection('facebook')?.connected_at)}
                    </div>
                    {getConnection('facebook')?.metadata?.pages?.length > 0 && (
                      <div className="mt-2 p-2 bg-green-50 rounded text-xs text-green-700">
                        ✓ {getConnection('facebook')?.metadata.pages.length} Facebook Page(s) connected
                      </div>
                    )}
                    {getConnection('facebook')?.metadata?.pages?.length === 0 && (
                      <div className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-700">
                        ⚠️ No Facebook Pages found. Create a Page to publish content.
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <button
                      onClick={() => handleConnect('facebook')}
                      disabled={connecting === 'facebook'}
                      className="w-full px-4 py-2.5 border-2 border-blue-500 text-blue-600 rounded-lg font-semibold hover:bg-blue-50 disabled:opacity-50 text-sm sm:text-base"
                    >
                      {connecting === 'facebook' ? 'Reconnecting...' : 'Reconnect'}
                    </button>
                    <button
                      onClick={() => handleDisconnect(getConnection('facebook')!.id, 'Facebook')}
                      className="w-full px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm sm:text-base"
                    >
                      Disconnect
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Features:</h4>
                    <ul className="space-y-1.5 text-xs sm:text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <span className="text-green-600 mt-0.5">✓</span>
                        <span>Publish to Facebook Pages</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-600 mt-0.5">✓</span>
                        <span>Share to Reels & Stories</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-600 mt-0.5">✓</span>
                        <span>Post to personal timeline</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-600 mt-0.5">✓</span>
                        <span>Full publishing control</span>
                      </li>
                    </ul>
                  </div>

                  <button
                    onClick={() => handleConnect('facebook')}
                    disabled={connecting === 'facebook'}
                    className="w-full px-4 sm:px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base"
                  >
                    {connecting === 'facebook' ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Connecting...</span>
                      </>
                    ) : (
                      <>
                        <span>Connect Facebook</span>
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd"/>
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {}
        <div className="mt-6 sm:mt-8">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Coming Soon</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {[
              { name: 'TikTok', key: 'tiktok', emoji: '', color: 'from-black to-gray-800' },
              { name: 'YouTube', key: 'youtube', emoji: '', color: 'from-red-600 to-red-500' },
              { name: 'Twitter/X', key: '', emoji: '𝕏', color: 'from-black to-gray-700' },
              { name: 'LinkedIn', key: '', emoji: '💼', color: 'from-blue-700 to-blue-600' }
            ].map((platform) => (
              <div key={platform.name} className="bg-white rounded-lg border p-3 sm:p-4 opacity-60">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br ${platform.color} rounded-lg flex items-center justify-center text-xl sm:text-2xl mb-2`}>
                  {platform.key ? (
                    <PlatformIcon platform={platform.key} className="w-6 h-6 sm:w-7 sm:h-7 fill-white"/>
                  ) : platform.emoji}
                </div>
                <div className="font-semibold text-gray-900 text-sm sm:text-base">{platform.name}</div>
                <div className="text-xs text-gray-500 mt-1">Coming Soon</div>
              </div>
            ))}
          </div>
        </div>

        {}
        <div className="mt-6 sm:mt-8 bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6">
          <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">Need Help?</h3>
          <div className="space-y-2 text-xs sm:text-sm text-gray-700">
            <p>• Instagram requires a Business Account connected to a Facebook Page</p>
            <p>• Facebook requires a Page to publish content (not personal profile)</p>
            <p>• All connections are secure and encrypted</p>
          </div>
          <div className="mt-3">
            <a
              href="https://help.instagram.com/502981923235522"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs sm:text-sm text-blue-600 hover:underline"
            >
              <span>Learn how to convert to Business Account</span>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}